import axios from "axios";
import api from "../api/api";
import { BASE_URL } from "../api/setup";
import { Callback, Livro } from "../types/general";
import useLivroStore from "../zustand/use-livro-store";

function useLivroModel() {

  const {
    livros,
    setLivros,
    setSelectedLivro,
    resetState,
    removeLivro,
    updateLivro,
  } = useLivroStore();

  // Fetch all livros with pagination
  const fetchLivros = async (page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/livros",
        params: { page },
      }, (data: any) => {
        setLivros(data);
        console.log(livros)
      }, (error: any) => {
        console.error("Error fetching livros:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching livros:", error);
      throw error;
    }
  };

  // Fetch livros by livraria
  const fetchLivrosByLivraria = async (livrariaId: number, page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/livros/livraria",
        params: { livraria: livrariaId, page },
      }, (data: any) => {
        setLivros(data);
      }, (error: any) => {
        console.error("Error fetching livros by livraria:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching livros by livraria:", error);
      throw error;
    }
  };

  // Fetch suggested livros
  const fetchSuggestions = async (page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/livros/suggestions",
        params: { page },
      }, (data: any) => {
        setLivros(data);
      }, (error: any) => {
        console.error("Error fetching suggestions:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      throw error;
    }
  };

  // Create new livro
  const createLivro = async (
    data: {
      title: string;
      shared?: string;
      url?: any;
      type?: 'BOOK' | 'AUX';
      author?: string;
      livrariaId: number
    },
    callback?: Callback
  ) => {
    try {
      // If PDF file is provided, use FormData
      const formData = new FormData();
      formData.append('title', data.title);
      if (data.shared) formData.append('shared', data.shared);
      if (data.type) formData.append('type', data.type);
      if (data.author) formData.append('author', data.author);
      formData.append('url', data.url);

      const response = await axios.post(`${BASE_URL}/api/livros`, formData, {
        params: { livraria: data.livrariaId }
      });

      setLivros({
        ...livros,
        data: [...[response.data], ...livros.data]
      })
      callback?.response?.(response.data);
      return response.data;
    } catch (error) {
      console.error("Error creating livro:", error);
      callback?.error?.(error);
      throw error;
    }
  };

  // Suggest new livro (visible = false)
  const suggestLivro = async (
    data: {
      title: string;
      shared?: string;
      url?: any;
      type?: 'BOOK' | 'AUX';
      author?: string;
      livrariaId: number;
    },
    callback?: Callback
  ) => {
    try {
      // If PDF file is provided, use FormData
      const formData = new FormData();
      formData.append('title', data.title);
      if (data.shared) formData.append('shared', data.shared);
      if (data.type) formData.append('type', data.type);
      if (data.author) formData.append('author', data.author);
      formData.append('url', data.url);

      api.post({
        path: "/api/livros/suggest",
        params: { livraria: data.livrariaId },
        data: formData,
      }, (responseData: any) => {
        callback?.response?.(responseData);
      }, (error: any) => {
        callback?.error?.(error);
      });

    } catch (error) {
      console.error("Error suggesting livro:", error);
      throw error;
    }
  };

  // Update existing livro
  const updateLivroById = async (
    livro: Livro,
    callback?: Callback
  ) => {
    try {
      api.put({
        path: "/api/livros",
        params: { livraria: livro.livraria_id, livro: livro.id },
        data: {
          title: livro.title,
          shared: livro.shared,
          type: livro.type,
          visible: livro.visible,
          author: livro.author,
        },
      }, (data: any) => {
        callback?.response?.(data);
        updateLivro(data);
      }, (error: any) => {
        console.log(error)
        callback?.error?.(error);
      });

    } catch (error) {
      console.error("Error updating livro:", error);
      throw error;
    }
  };

  // Validate/approve suggested livro
  const validateLivro = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/livros/validate/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updateLivro({
          ...(livros.data.find((item) => item.id === id) as Livro),
          visible: true
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error validating livro:", error);
      throw error;
    }
  };

  // Reject livro
  const rejectLivro = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/livros/reject/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updateLivro({
          ...(livros.data.find((item) => item.id === id) as Livro),
          visible: false
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error rejecting livro:", error);
      throw error;
    }
  };

  // Delete livro
  const deleteLivroById = async (id: number, callback: Callback) => {
    try {
      await api.delete({
        path: "/api/livros",
        params: { livro: id },
      }, (data: any) => {
        callback.response?.(data);
        removeLivro(id);
      }, (error: any) => {
        callback.error?.(error);
      });
    } catch (error) {
      console.error("Error deleting livro:", error);
      throw error;
    }
  };

  return {
    fetchLivros,
    fetchLivrosByLivraria,
    fetchSuggestions,
    createLivro,
    suggestLivro,
    updateLivroById,
    validateLivro,
    rejectLivro,
    deleteLivroById,
  };
}

export default useLivroModel;
